import { useState, type ChangeEvent, type ReactNode } from 'react';
import { translateCountry, type CountryIso, type LocaleIso } from 'utils';
import ComboBox from '@pixum/combobox';
import { TelephoneInput, TextInput } from '@pixum/web-input';
import Text, { TEXT_TAG, TEXT_VARIANT } from '@pixum/text';
import { LOCALES_REQUIRING_PHONE_NUMBER } from '../../../constants';
import type { AddressFormData } from '../../../types';
import { PhoneNumberHint } from '../../shared/PhoneNumberHint';
import styles from '../../../CartAddressStep.module.css';
import { getCountryData } from '../utils';

interface AddressLocationFieldsProps {
  formData: AddressFormData;
  constants: Record<string, string>;
  currentCountry: CountryIso;
  fieldError: (name: string) => string | undefined;
  fieldName: (name: string) => string;
  formId?: string;
  isCountryReadOnly: boolean;
  isInDialog: boolean;
  locale: LocaleIso;
  onCountryChange: (country: string) => void;
  onInputChange: (event: ChangeEvent<HTMLInputElement>) => void;
  pickupPointSelector?: ReactNode;
  showCountryField: boolean;
  showPhoneField: boolean;
  submittedCountry: string;
}

function isPhoneRequired(locale: LocaleIso) {
  return LOCALES_REQUIRING_PHONE_NUMBER.includes(locale);
}

function translateCountryName(locale: LocaleIso, country: CountryIso) {
  return translateCountry(locale, country);
}

export function AddressLocationFields({
  formData,
  constants,
  currentCountry,
  fieldError,
  fieldName,
  formId,
  isCountryReadOnly,
  isInDialog,
  locale,
  onCountryChange,
  onInputChange,
  pickupPointSelector,
  showCountryField,
  showPhoneField,
  submittedCountry
}: AddressLocationFieldsProps) {
  const [isCountrySelectionOpen, setIsCountrySelectionOpen] = useState(false);
  const countryData = getCountryData(locale, currentCountry);
  const countryOptions = countryData.map(({ text }) => ({ text }));
  const selectedCountry = countryData.find(option => option.id === submittedCountry.toLowerCase())?.text ?? '';

  const handleCountrySelection = (value: string) => {
    onCountryChange(countryData.find(option => option.text === value)?.id ?? '');
    setIsCountrySelectionOpen(false);
  };

  return (
    <>
      <div className={styles.cartAddressTwoColumns}>
        <TextInput
          label={constants['i18n_postcode']}
          form={formId}
          name={fieldName('zip')}
          value={formData.zip}
          onChange={onInputChange}
          hasError={Boolean(fieldError('zip') || fieldError('postcode'))}
          inputInfo={fieldError('zip') ?? fieldError('postcode')}
        />
        <TextInput
          label={constants['i18n_city']}
          form={formId}
          name={fieldName('city')}
          value={formData.city}
          onChange={onInputChange}
          hasError={Boolean(fieldError('city'))}
          inputInfo={fieldError('city')}
        />
      </div>
      {pickupPointSelector}
      {showCountryField && (
        <>
          {!isCountryReadOnly ? (
            <ComboBox
              bottomSheetBackdropClassName={isInDialog ? styles.cartAddressDialogBackdrop : undefined}
              hasError={Boolean(fieldError('country'))}
              helperText={fieldError('country')}
              isOpen={isCountrySelectionOpen}
              label={constants['i18n_country']}
              onClick={() => setIsCountrySelectionOpen(open => !open)}
              onOptionItemClick={handleCountrySelection}
              onOutsideClick={() => setIsCountrySelectionOpen(false)}
              options={countryOptions}
              setIsOpen={setIsCountrySelectionOpen}
              value={selectedCountry}
              withSearch={false}
              placeholder={constants['i18n_gender_placeholder']}
              headline={constants['i18n_country']}
            />
          ) : (
            <div className={styles.readOnlyField}>
              <Text variant={TEXT_VARIANT.CAPTION_XS} tag={TEXT_TAG.DIV}>
                {constants['i18n_country']}
              </Text>
              <Text variant={TEXT_VARIANT.INPUT} tag={TEXT_TAG.DIV}>
                {translateCountryName(locale, currentCountry)}
              </Text>
            </div>
          )}
          <input name={fieldName('country')} type="hidden" value={submittedCountry} />
        </>
      )}
      {showPhoneField && (
        <TelephoneInput
          label={constants['i18n_phone_number']}
          form={formId}
          name={fieldName('phone')}
          value={formData.phone}
          onChange={onInputChange}
          required={isPhoneRequired(locale)}
          hasError={Boolean(fieldError('phone'))}
          inputInfo={fieldError('phone')}
          additionalLabelInfo={isPhoneRequired(locale) ? undefined : constants['i18n_optional']}
          additionalInputInfo={<PhoneNumberHint constants={constants} />}
        />
      )}
    </>
  );
}
